import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, Plus, MoreVertical, Edit, Trash2, Loader2, Minus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { useActivities } from '@/hooks/use-activities';
import { AddActivityDialog } from './AddActivityDialog';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Activity as ActivityType } from '@/lib/firebase/queries/activities';

interface RecordingActivityWidgetProps {
  organizationId?: string;
}

export function RecordingActivityWidget({ organizationId }: RecordingActivityWidgetProps) {
  const { t, dir } = useLanguage();
  const { activities, loading, addActivity, editActivity, removeActivity } = useActivities();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingActivity, setEditingActivity] = useState<ActivityType | null>(null);

  const handleDialogSuccess = async (
    name: string,
    startTime: string,
    endTime: string,
    notes?: string,
    calories?: number
  ) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    if (editingActivity) {
      await editActivity(editingActivity.id, { name, startTime: start, endTime: end, notes, calories });
      setEditingActivity(null);
    } else {
      await addActivity({ name, startTime: start, endTime: end, notes, calories });
      setShowAddDialog(false);
    }
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (confirm(t('activities.confirmDelete') || 'Are you sure you want to delete this activity?')) {
      await removeActivity(activityId);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            {t('dashboard.customer.dashboard.recordingActivity.title')}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddDialog(true)}
            className="bg-white hover:bg-gray-100"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">{t('dashboard.customer.dashboard.recordingActivity.addActivity')}</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('activities.noActivities') || 'No activities logged today'}
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#45B7D1]/15 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-[#45B7D1]" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{activity.name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="text-sm text-muted-foreground">
                      {format(activity.startTime, 'p')} - {format(activity.endTime, 'p')}
                    </div>
                    {activity.calories !== undefined && activity.calories !== null && (
                      <Badge variant="outline" className="text-xs bg-[#7dda55]/10 text-[#7dda55] border-[#7dda55]/20">
                        <Minus className="h-3 w-3 mr-1" />
                        {activity.calories} kcal
                      </Badge>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={dir === 'rtl' ? 'start' : 'end'}>
                    <DropdownMenuItem onClick={() => setEditingActivity(activity)}>
                      <Edit className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                      {t('common.edit') || 'Edit'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDeleteActivity(activity.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                      {t('common.delete') || 'Delete'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </motion.div>
            ))}
          </div>
        )}
      </CardContent>

      <AddActivityDialog
        open={showAddDialog || !!editingActivity}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false);
            setEditingActivity(null);
          }
        }}
        onSuccess={handleDialogSuccess}
        activity={editingActivity}
      />
    </Card>
  );
}

